import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, FileText } from 'lucide-react';
import ImageUpload from '@/components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const emptyBlog = { title: '', slug: '', content: '', excerpt: '', image_url: '', author: 'Dr. Preet Clinic', published: true };

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBlog);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/admin/blogs`, authHeaders()).then(r => setBlogs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyBlog); setDialogOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ title: b.title, slug: b.slug, content: b.content, excerpt: b.excerpt || '', image_url: b.image_url || '', author: b.author || 'Dr. Preet Clinic', published: b.published !== false }); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/blogs/${editing.id}`, form, authHeaders());
      } else {
        await axios.post(`${API}/admin/blogs`, form, authHeaders());
      }
      setDialogOpen(false); load();
    } catch { alert('Error saving blog'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    await axios.delete(`${API}/admin/blogs/${id}`, authHeaders());
    load();
  };

  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <div data-testid="admin-blogs-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-2xl font-medium">Blog Posts</h1>
        <Button className="rounded-xl" onClick={openCreate} data-testid="add-blog-btn"><Plus className="w-4 h-4 mr-2" /> Add Post</Button>
      </div>

      <div className="space-y-4">
        {blogs.map((b, i) => (
          <Card key={b.id} className="rounded-2xl border-border" data-testid={`admin-blog-card-${i}`}>
            <CardContent className="p-5 flex items-center gap-4">
              {b.image_url && <img src={b.image_url} alt="" className="w-20 h-14 rounded-xl object-cover flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-medium truncate">{b.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${b.published ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                    {b.published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{b.author} &bull; {new Date(b.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openEdit(b)} data-testid={`edit-blog-${i}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="outline" size="sm" className="rounded-lg text-destructive hover:bg-destructive hover:text-white" onClick={() => handleDelete(b.id)} data-testid={`delete-blog-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {blogs.length === 0 && (
        <div className="text-center py-16">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No blog posts yet. Create your first post.</p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="blog-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit Blog Post' : 'Add Blog Post'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm">Title</Label>
              <Input value={form.title} onChange={e => { updateForm('title', e.target.value); if (!editing) updateForm('slug', generateSlug(e.target.value)); }} className="mt-1 rounded-xl" data-testid="blog-title-input" />
            </div>
            <div><Label className="text-sm">Slug</Label><Input value={form.slug} onChange={e => updateForm('slug', e.target.value)} className="mt-1 rounded-xl" data-testid="blog-slug-input" /></div>
            <div><Label className="text-sm">Excerpt</Label><Textarea value={form.excerpt} onChange={e => updateForm('excerpt', e.target.value)} rows={2} className="mt-1 rounded-xl" data-testid="blog-excerpt-input" /></div>
            <div><Label className="text-sm">Content</Label><Textarea value={form.content} onChange={e => updateForm('content', e.target.value)} rows={8} className="mt-1 rounded-xl" data-testid="blog-content-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm">Author</Label><Input value={form.author} onChange={e => updateForm('author', e.target.value)} className="mt-1 rounded-xl" data-testid="blog-author-input" /></div>
              <div />
            </div>
            <ImageUpload value={form.image_url} onChange={v => updateForm('image_url', v)} label="Blog Image" />
            <div className="flex items-center gap-3">
              <Switch checked={form.published} onCheckedChange={v => updateForm('published', v)} data-testid="blog-published-switch" />
              <Label className="text-sm">Published</Label>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full rounded-xl" data-testid="save-blog-btn">
              {saving ? 'Saving...' : editing ? 'Update Post' : 'Create Post'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
